<template>
  <config-base class="opc-rc">
    <div slot="config" class="opc-rc__config" ref="config">
      <div class="opc-rc__quantity">
        <input-number-with-popover
          class="opc-rc__quantity-selector"
          v-model="selectedNumber"
          size="large"
          controls-position="both"
          :min="minHosts"
          :max="maxHosts"
          @change="updatePlanLicense"
          :decrease-tips="decreaseTips"
          :unit="$t('billing.rc.license_unit')"
          :label="`Select the number of ports`"
        />
        <zm-tooltip class="opc-rc__license-tooltip"
          aria-label="Learn more about Select the number of ports"
          :content="$t('billing.rc.license_tooltips')" placement="top">
          <i class="zm-icon-info-outline main-icon" role="button" @mouseover="() => $tracking.buyInteraction(trackingPlanName + ' options', 'number of ports - tooltip')" @click="() => $tracking.buyInteraction(trackingPlanName + ' options', 'number of ports - tooltip')"></i>
        </zm-tooltip>
      </div>
      <bought-license v-if="showBoughtLicense" :type="planType"/>
      <alert type="warning" class="opc-rc__license-alert" v-if="showMaxLicenseTips">
        <p class="opc-rc__license-alert-tips" v-html="$t('billing.rc.max_license_alert', [maxHosts])" />
      </alert>
      <config-cycle
        v-model="billingCycle"
        :billing-cycle-list="billingCycleList"
        @switchCycleAction="switchCycleAction"
        :unit="planUnitMap.port"
        :show-cycle-alert="showCycleAlert"
        :trackingPlanName="trackingPlanName"
      />
      <preview-payment class="opc-rc__preview-payment" />
    </div>
    <template #footer v-if="canRmv">
      <zm-button type="link" ghost @click="removePlanByKey" :aria-label="'remove ' + trackingPlanName + ' plan'">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import mx_plansCommon from '@/plans/mx/plan';
import mx_config from '@/plans/ROOM_CONNECTOR/mixin';
import ConfigBase from '@/components/config-base';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { DEFAULT_MIN_HOST, trackingPlanTypes } from '@/common/constants';
import { plansof } from '@/utils/chain';
import ConfigCycle from '@/components/service/config-billing-cycle/index';
import { planUnitMap } from '@/common/product-unit';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import { logger } from '@/utils/log';
import AddonMixin from '@/plans/mx/addon';
import { isEmptyVal } from '@/utils';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';

const ConfigBaseComp = {
  name: 'rc-base',
  extends: ConfigBase,
  mixins: [mx_config],
  data() {
    return {
      showLearnMore: false,
      configDescription: this.$t('billing.rc.desc'),
      unit: planUnitMap.port,
      showPlanDetails: false,
    };
  },
};

export default {
  mixins: [mx_plansCommon, mx_config, AddonMixin],
  components: {
    PreviewPayment,
    Alert,
    ConfigCycle,
    InputNumberWithPopover,
    ConfigBase: ConfigBaseComp,
    BoughtLicense,
  },
  data() {
    return {
      selectedNumber: 1,
      billingCycle: '',
      trackingPlanName: trackingPlanTypes.ROOM_CONNECTOR,
      planUnitMap,
      showCycleAlert: true,
      showBoughtLicense: false,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getSelectedPlansMap', 'planMapFlat', 'getUserCurrentSubscriptions', 'getCurrentBasePlanInfo', 'getTypeGroupSelectedPlans']),
    planList() {
      return plansof(this.planMapFlat).type(this.planType).get();
    },
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
    mx_minHosts() {
      return _.get(this.planList, [0, 'min'], DEFAULT_MIN_HOST);
    },
    minHosts() {
      return this.mx_minHosts;
    },
    maxHosts() {
      return _.get(this.planList, [0, 'max'], 9999);
    },
    decreaseTips() {
      return '';
    },
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = _.get(this.planList, [0, 'billingCycleDependBasePlanTypeList'], []);

      return _.chain(this.getUserCurrentSubscriptions)
        .filter((p) => _.includes(dependCyclePlanList, p.type))
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },
    depCurrentAllAddonPlanList() {
      return _.filter(this.getUserCurrentSubscriptions,
        (p) => _.includes(p.dependBasePlanTypeList, this.planType));
    },
    disabledForBulkLicense() {
      return (cycle) => {
        logger.info("bulk-license", cycle);
        return false;
      }
    },
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },
    basePlanAlreadyInCart() {
      const configPlan = plansof(this.getSelectedPlans).type(this.$context.type).get(0);
      const { dependBasePlanTypeList = [] } = configPlan;
      return plansof(this.getSelectedPlans).filter(basePlan => dependBasePlanTypeList.includes(basePlan.type)).has();
    },
    canRmv() {
      return false;
    },
    showMaxLicenseTips() {
      return this.selectedNumber >= this.maxHosts;
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),
    updatePlanLicense: _.debounce(function(newPorts, oldPorts) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newPorts },
      });
      this.mx_validDownSellLicense(newPorts)

      // event tracking
      this.$tracking.buyInteraction(
        `number of ports`,
        `${this.trackingPlanName} - ${newPorts > oldPorts ? 'increase' : 'decrease'} - ${newPorts}`,
      );
    }, 500),
    async switchCycleAction(oldPlan, newPlan) {
      // when the current license less than max, get current license, otherwise get maxHosts
      const quantity = this.selectedNumber <= this.maxHosts ? this.selectedNumber : this.maxHosts;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        newPlanInfo,
      ]);

      this.afterSwitchCycleTracking();
    },
    afterSwitchCycleTracking() {
      // event tracking
      const billingCycle = this.selectedPlan?.billing_cycle;
      billingCycle && this.$tracking.buyInteraction(
        `${this.trackingPlanName} plan`,
        this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
      );
    }
  },
  created() {
    const selectedPlan = this.selectedPlan;
    if (!isEmptyVal(selectedPlan)) {
      this.billingCycle = selectedPlan.billing_cycle;
      this.selectedNumber = selectedPlan.cartInfo?.selected_number;
    }

    // refresh page
    this.$nextTick(() => {
      this.mx_validDownSellLicense(this.selectedNumber);
    });
  },
  watch: {
    showMaxLicenseTips: {
      immediate: true,
      handler(val) {
        val && this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingPlanName} - the maximum amount is ${this.maxHosts} licenses - impression`
        );
      },
    }
  }
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-rc {
  .opc-zm-base-card__name {
    @include media(mobile) {
      display: block;
      padding-right: 60px;
    }
  }

  &__quantity {
    display: flex;
    align-items: center;
    &-unit {
      margin-left: 8px;
      color: #00053D;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%; /* 21px */
    }
  }

  &__license-alert {
    &.zm-alert {
      margin-top: 12px;
    }

    &-tips {
      color: #666487;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%; /* 21px */

      .opc-rc-max-license-alert {
        color: #0B5CFF;
        font-weight: 600;
        text-transform: capitalize;
      }
    }
  }

  &__license-tooltip {
    margin-left: 4px;
    cursor: pointer;
    color: #0B5CFF;
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
